/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermission, type ControllerPermission, getOrgScopeModulePermission } from '@/hooks/usePermission';
import { getControllerFromRoute } from '@/helper/RouteHelper';
import { resolveEffectivePathname } from '@/components/PhanHe/VanHoaPhanHeHandler';
import { normalizeModuleController, resolveOrgScope, type OrgScope } from '@/utils/orgScope';

/** BSC/QT: dùng pathname gốc. Chỉ VH cần asPath khi Next.js transient `/404`. */
const resolveControllerPathname = (pathname: string, asPath: string) => {
  const normalized = pathname.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  if (normalized === '/404') {
    return resolveEffectivePathname(pathname, asPath);
  }
  return pathname;
};

export type OrgScopeContextValue = {
  controller: string | null;
  orgScope: OrgScope;
  modulePerm: ControllerPermission | null;
  profile: any;
};

const OrgScopeContext = createContext<OrgScopeContextValue | null>(null);

export const OrgScopeProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const profile = useAuthStore((s) => s.profile);

  const controller = useMemo(
    () => normalizeModuleController(getControllerFromRoute(resolveControllerPathname(router.pathname, router.asPath))),
    [router.pathname, router.asPath],
  );

  const modulePerm = usePermission(controller ?? undefined);
  const orgScope = useMemo(() => {
    const orgModulePerm = getOrgScopeModulePermission(profile, controller);
    return resolveOrgScope(profile, orgModulePerm, controller);
  }, [profile, controller]);

  const value = useMemo(
    () => ({ controller, orgScope, modulePerm, profile }),
    [controller, orgScope, modulePerm, profile]
  );

  return <OrgScopeContext.Provider value={value}>{children}</OrgScopeContext.Provider>;
};

export const useOrgScope = (): OrgScopeContextValue => {
  const ctx = useContext(OrgScopeContext);
  if (!ctx) {
    throw new Error('useOrgScope must be used within OrgScopeProvider');
  }
  return ctx;
};

export const useOrgScopeOptional = (): OrgScopeContextValue | null => useContext(OrgScopeContext);
